// New Client wizard (#510) — the operator's Twilio-first onboarding flow.
// Department → Twilio subaccount (auto, #509) → Numbers (buy) → Trunk
// auto-configure → E911 → SMS lane → Users → Guardrails → Review.
// Staff never opens the Twilio console: the subaccount, API key, numbers and
// elastic trunk are all created through the operator vault (#508 seam).
// Progress persists per department in integration_settings slug 'onboarding'
// (GET/PUT /api/onboard/state), so a half-finished client is resumable — every
// backend step is idempotent, so re-running a completed step is harmless.

import { useEffect, useState } from 'react';
import {
  Sparkles, Building2, KeyRound, Hash, PhoneCall, MapPin, MessageSquare,
  Users as UsersIcon, Gauge, ClipboardCheck, CheckCircle2, ChevronLeft,
  ChevronRight, X, Plus, Trash2, ExternalLink, Loader2, Info, ShieldAlert,
  BadgeCheck, ArrowRightLeft,
} from 'lucide-react';
import { api, getToken } from '../lib/api';
import { SmsLanePicker, QuickLane, DedicatedLane } from './SmsProvision';

type Props = { open: boolean; onClose: () => void; onDone?: () => void };

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });
async function jsend(url: string, method: string, body?: any) {
  const opts: any = { method, headers: { ...authHeaders() } };
  if (body !== undefined && method !== 'GET') { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
  const r = await fetch(url, opts);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || r.statusText);
  return j;
}

const STEP_DEFS: { key: string; label: string; icon: any }[] = [
  { key: 'department', label: 'Department',  icon: Building2 },
  { key: 'twilio',     label: 'Twilio',      icon: KeyRound },
  { key: 'numbers',    label: 'Numbers',     icon: Hash },
  { key: 'trunk',      label: 'Trunk',       icon: PhoneCall },
  { key: 'e911',       label: 'E911',        icon: MapPin },
  { key: 'sms',        label: 'SMS',         icon: MessageSquare },
  { key: 'users',      label: 'Users',       icon: UsersIcon },
  { key: 'guardrails', label: 'Guardrails',  icon: Gauge },
  { key: 'cnam',       label: 'Caller ID',   icon: BadgeCheck },
  { key: 'review',     label: 'Review',      icon: ClipboardCheck },
];

export function NewClientWizard({ open, onClose, onDone }: Props) {
  const [stepIdx, setStepIdx] = useState(0);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Department (create new or resume an existing one)
  const [tenants, setTenants] = useState<any[]>([]);
  const [pickExisting, setPickExisting] = useState('');
  const [deptName, setDeptName] = useState('');
  const [domainUuid, setDomainUuid] = useState('');

  // Twilio subaccount (P1)
  const [vault, setVault] = useState<any>(null);
  const [twilioResult, setTwilioResult] = useState<any>(null);

  // Numbers
  const [areaCode, setAreaCode] = useState('');
  const [found, setFound] = useState<any[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState('');
  const [bought, setBought] = useState<any[]>([]);

  // Trunk auto-configure
  const [trunkResult, setTrunkResult] = useState<any>(null);

  // SMS lane
  const [lane, setLane] = useState('');
  const [smsDone, setSmsDone] = useState(false);

  // Users
  const [users, setUsers] = useState<any[]>([{ first_name: '', last_name: '', email: '' }]);
  const [createdUsers, setCreatedUsers] = useState<any[]>([]);

  // Guardrails (P5) — thresholds + trigger install state for this dept
  const [guard, setGuard] = useState<any>(null);
  const [guardAlert, setGuardAlert] = useState('');
  const [guardSuspend, setGuardSuspend] = useState('');

  // Port-in (P3) — intake + tracker inside the Numbers step
  const [portOpen, setPortOpen] = useState(false);
  const [portForm, setPortForm] = useState<any>({ numbers: '', account_number: '', pin: '', authorized_person: '', notification_email: '', street: '', city: '', region: '', postal_code: '' });
  const [portChecks, setPortChecks] = useState<any[] | null>(null);
  const [portRequests, setPortRequests] = useState<any[]>([]);

  // CNAM (P6) + E911 gate for Review
  const [cnam, setCnam] = useState<any>(null);
  const [cnamName, setCnamName] = useState('');
  const [e911Ready, setE911Ready] = useState<any>(null);

  const step = Math.min(stepIdx, STEP_DEFS.length - 1);
  const key = STEP_DEFS[step].key;
  const next = () => { setErr(''); setStepIdx((s) => Math.min(s + 1, STEP_DEFS.length - 1)); };
  const back = () => { setErr(''); setStepIdx((s) => Math.max(s - 1, 0)); };

  // Reset on open + load the dept list and the vault capability flags (#508)
  useEffect(() => {
    if (!open) return;
    setStepIdx(0); setErr(''); setBusy(false);
    setPickExisting(''); setDeptName(''); setDomainUuid('');
    setVault(null); setTwilioResult(null);
    setAreaCode(''); setFound(null); setPicked(''); setBought([]);
    setTrunkResult(null); setLane(''); setSmsDone(false);
    setUsers([{ first_name: '', last_name: '', email: '' }]); setCreatedUsers([]);
    setGuard(null); setGuardAlert(''); setGuardSuspend('');
    setPortOpen(false); setPortChecks(null); setPortRequests([]);
    setPortForm({ numbers: '', account_number: '', pin: '', authorized_person: '', notification_email: '', street: '', city: '', region: '', postal_code: '' });
    setCnam(null); setCnamName(''); setE911Ready(null);
    api.tenants.list().then((t: any) => setTenants(t || [])).catch(() => {});
    jsend('/api/onboard/vault-status', 'GET').then(setVault).catch(() => setVault({ configured: false }));
  }, [open]);

  // Step-scoped loads: port tracker (Numbers), CNAM status, E911 go-live gate (Review).
  useEffect(() => {
    if (!open || !domainUuid) return;
    if (key === 'numbers') jsend(`/api/onboard/port-in?domain_uuid=${domainUuid}`, 'GET').then((r) => setPortRequests(r.requests || [])).catch(() => {});
    if (key === 'cnam') jsend(`/api/onboard/cnam?domain_uuid=${domainUuid}`, 'GET').then(setCnam).catch(() => setCnam({ registered: false }));
    if (key === 'review') {
      jsend(`/api/onboard/e911-status?domain_uuid=${domainUuid}`, 'GET').then(setE911Ready).catch(() => setE911Ready({ ready: false }));
      jsend(`/api/onboard/cnam?domain_uuid=${domainUuid}`, 'GET').then(setCnam).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key, domainUuid]);

  // Load guardrail state when the step opens (triggers auto-install with the
  // subaccount in P1; this shows the outcome and lets the operator adjust).
  useEffect(() => {
    if (!open || key !== 'guardrails' || !domainUuid) return;
    jsend(`/api/onboard/guardrails?domain_uuid=${domainUuid}`, 'GET').then((g) => {
      setGuard(g);
      setGuardAlert(String(g.alert_usd ?? g.defaults?.alert_usd ?? 50));
      setGuardSuspend(String(g.suspend_usd ?? g.defaults?.suspend_usd ?? 200));
    }).catch(() => setGuard({ installed: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key, domainUuid]);

  if (!open) return null;

  // Persist a shallow patch of wizard progress; the server merges it into the
  // per-dept jsonb state. Best-effort — never blocks the flow.
  function saveState(dom: string, patch: any) {
    if (!dom) return;
    jsend('/api/onboard/state', 'PUT', { domain_uuid: dom, state: patch }).catch(() => {});
  }

  // Resume: hydrate whatever a previous run saved for this dept and jump to
  // the first step after the last completed one.
  async function resumeDept(dom: string, name: string) {
    setDomainUuid(dom); setDeptName(name);
    try {
      const r = await jsend(`/api/onboard/state?domain_uuid=${dom}`, 'GET');
      const s = r.state || {};
      if (s.twilio)  setTwilioResult(s.twilio);
      if (Array.isArray(s.numbers)) setBought(s.numbers);
      if (s.trunk)   setTrunkResult(s.trunk);
      if (s.lane)    setLane(s.lane);
      if (s.sms_done) setSmsDone(true);
      if (Array.isArray(s.users)) setCreatedUsers(s.users);
      const idx = STEP_DEFS.findIndex((d) => d.key === s.step);
      setStepIdx(idx >= 0 ? Math.min(idx + 1, STEP_DEFS.length - 1) : 1);
    } catch { setStepIdx(1); }
  }

  async function createDepartment() {
    setErr('');
    if (pickExisting) {
      const t = tenants.find((x: any) => x.domain_uuid === pickExisting);
      setBusy(true);
      await resumeDept(pickExisting, t?.domain_description || t?.domain_name || '');
      setBusy(false);
      return;
    }
    if (!deptName.trim()) return setErr('Department name is required.');
    setBusy(true);
    try {
      const realm = `pending-onboard-${Math.random().toString(36).slice(2, 8)}`;
      const r: any = await api.tenants.create({
        domain_name: realm,
        domain_description: deptName.trim(),
        domain_enabled: 'true',
        extension_floor: 2000, extension_ceiling: 3000,
        system_extension_floor: 8000, system_extension_ceiling: 9000,
        trunk_limit: 6, transcription_mode: 'none',
      });
      setDomainUuid(r.domain_uuid);
      saveState(r.domain_uuid, { step: 'department', department: { name: deptName.trim() } });
      next();
    } catch (e: any) { setErr(e?.message || 'Failed to create department'); }
    finally { setBusy(false); }
  }

  async function createSubaccount() {
    setErr(''); setBusy(true);
    try {
      const r = await jsend('/api/onboard/twilio-subaccount', 'POST', {
        domain_uuid: domainUuid, friendly_name: deptName || 'Stratus client',
      });
      setTwilioResult(r);
      saveState(domainUuid, { step: 'twilio', twilio: { account_sid: r.account_sid, api_key_sid: r.api_key_sid } });
    } catch (e: any) { setErr(e?.message || 'Subaccount creation failed'); }
    finally { setBusy(false); }
  }

  async function searchNumbers() {
    setSearching(true); setFound(null); setPicked('');
    try {
      const r = await jsend(`/api/integrations/twilio/available-numbers?domain_uuid=${domainUuid}${areaCode ? `&area_code=${areaCode}` : ''}`, 'GET');
      setFound(r.numbers || []);
    } catch (e: any) { setErr(e?.message || 'Number search failed'); }
    finally { setSearching(false); }
  }

  async function buyNumber() {
    setErr(''); setBusy(true);
    try {
      const body: any = { domain_uuid: domainUuid };
      if (picked) body.phone_number = picked; else if (areaCode) body.area_code = areaCode;
      const r = await jsend('/api/integrations/twilio/buy-number', 'POST', body);
      const nums = [...bought, { phone_number: r.phone_number, sid: r.sid }];
      setBought(nums); setPicked(''); setFound(null);
      saveState(domainUuid, { step: 'numbers', numbers: nums });
    } catch (e: any) { setErr(e?.message || 'Purchase failed'); }
    finally { setBusy(false); }
  }

  async function configureTrunk() {
    setErr(''); setBusy(true);
    try {
      const r = await jsend('/api/integrations/twilio/configure', 'POST', {
        domain_uuid: domainUuid, number_sids: bought.map((n) => n.sid),
      });
      setTrunkResult(r);
      saveState(domainUuid, { step: 'trunk', trunk: { ok: !!r.ok, trunk_sid: r.trunk_sid || null } });
    } catch (e: any) { setErr(e?.message || 'Trunk auto-configure failed'); }
    finally { setBusy(false); }
  }

  async function createUsers() {
    setErr('');
    const rows = users.filter((u) => `${u.first_name}${u.last_name}${u.email}`.trim());
    if (!rows.length) return next();
    setBusy(true);
    const made: any[] = [];
    try {
      for (const u of rows) {
        const nx: any = await api.users.nextExtension(domainUuid);
        const ext = nx?.next;
        const res: any = await api.users.create({ extension: ext, first_name: u.first_name, last_name: u.last_name, email: u.email, domain_uuid: domainUuid });
        made.push({ name: `${u.first_name} ${u.last_name}`.trim() || ext, extension: res?.extension || ext });
      }
      const all = [...createdUsers, ...made];
      setCreatedUsers(all);
      saveState(domainUuid, { step: 'users', users: all });
      next();
    } catch (e: any) { setCreatedUsers((prev) => [...prev, ...made]); setErr(e?.message || 'Failed while creating users'); }
    finally { setBusy(false); }
  }

  async function saveGuardrails() {
    setErr(''); setBusy(true);
    try {
      const r = await jsend('/api/onboard/guardrails', 'PUT', {
        domain_uuid: domainUuid, alert_usd: Number(guardAlert), suspend_usd: Number(guardSuspend),
      });
      setGuard({ ...guard, installed: r.installed, install_error: r.install_error, alert_usd: Number(guardAlert), suspend_usd: Number(guardSuspend) });
      saveState(domainUuid, { step: 'guardrails', guardrails: { alert_usd: Number(guardAlert), suspend_usd: Number(guardSuspend), installed: r.installed } });
      if (!r.installed) setErr(r.install_error || 'Trigger install failed');
    } catch (e: any) { setErr(e?.message || 'Failed to save guardrails'); }
    finally { setBusy(false); }
  }

  const portNums = () => portForm.numbers.split(/[,\s]+/).map((s: string) => s.trim()).filter(Boolean);

  async function checkPortability() {
    setErr(''); setBusy(true); setPortChecks(null);
    try {
      const r = await jsend('/api/onboard/port-in/check', 'POST', { phone_numbers: portNums() });
      setPortChecks(r.results || []);
    } catch (e: any) { setErr(e?.message || 'Portability check failed'); }
    finally { setBusy(false); }
  }

  async function filePortIn() {
    setErr(''); setBusy(true);
    try {
      const r = await jsend('/api/onboard/port-in', 'POST', {
        domain_uuid: domainUuid, phone_numbers: portNums(),
        account_number: portForm.account_number, pin: portForm.pin,
        authorized_person: portForm.authorized_person, notification_email: portForm.notification_email,
        address: { street: portForm.street, city: portForm.city, region: portForm.region, postal_code: portForm.postal_code },
      });
      setPortRequests((prev) => [...prev, { sid: r.sid, phone_numbers: r.phone_numbers, status: 'pending' }]);
      setPortChecks(null);
      setPortForm({ ...portForm, numbers: '' });
      saveState(domainUuid, { port_in_filed: true });
    } catch (e: any) { setErr(e?.message || 'Port-in filing failed'); }
    finally { setBusy(false); }
  }

  async function registerCnam() {
    setErr(''); setBusy(true);
    try {
      const r = await jsend('/api/onboard/cnam', 'POST', { domain_uuid: domainUuid, display_name: cnamName });
      setCnam({ registered: true, display_name: cnamName, status: r.status, trust_product_sid: r.trust_product_sid });
      saveState(domainUuid, { step: 'cnam', cnam: { display_name: cnamName, status: r.status } });
    } catch (e: any) { setErr(e?.message || 'CNAM registration failed'); }
    finally { setBusy(false); }
  }

  function finish() {
    saveState(domainUuid, { step: 'review', done: true });
    onDone?.(); onClose();
  }

  const canCreateSub = !!vault?.capabilities?.can_create_subaccounts;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal>
      <div className="card w-full max-w-3xl max-h-[92vh] flex flex-col p-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-semibold flex items-center gap-2 text-lg">
            <Sparkles size={20} className="text-primary" /> New Client
            <span className="text-textSecondary text-sm font-normal">· Twilio-first onboarding{deptName ? ` · ${deptName}` : ''}</span>
          </h2>
          <button onClick={onClose} className="btn-ghost p-1" title="Close"><X size={18} /></button>
        </div>

        {/* Stepper */}
        <div className="px-4 pt-3">
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {STEP_DEFS.map((s, i) => {
              const Icon = s.icon; const active = i === step; const done = i < step;
              return (
                <div key={s.key} className="flex items-center gap-1 shrink-0">
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs whitespace-nowrap
                    ${active ? 'bg-primary text-white' : done ? 'text-success' : 'text-textSecondary'}`}>
                    {done ? <CheckCircle2 size={14} /> : <Icon size={14} />} {s.label}
                  </div>
                  {i < STEP_DEFS.length - 1 && <ChevronRight size={12} className="text-textSecondary" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="overflow-auto p-5 flex-1 space-y-4">
          {err && <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2">{err}</div>}

          {key === 'department' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Which client are we onboarding?</h3>
              <L label="Resume an existing department">
                <select className="input" value={pickExisting} onChange={(e) => { setPickExisting(e.target.value); if (e.target.value) setDeptName(''); }}>
                  <option value="">— start a brand-new client —</option>
                  {tenants.map((t: any) => (
                    <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>
                  ))}
                </select>
              </L>
              {!pickExisting && (
                <L label="New client / department name *">
                  <input className="input" autoFocus value={deptName} placeholder="Acme Plumbing" onChange={(e) => setDeptName(e.target.value)} />
                </L>
              )}
              <div className="text-xs text-textSecondary bg-elevated border border-border rounded-md px-3 py-2 flex gap-2">
                <Info size={14} className="shrink-0 mt-0.5 text-primary" />
                <span>Picking an existing department resumes its saved onboarding progress. A new client gets its own department (tenant) with a temporary realm — set the phone realm later from the SBC or Tenants page.</span>
              </div>
            </div>
          )}

          {key === 'twilio' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Twilio subaccount — automatic</h3>
              <p className="text-sm text-textSecondary">Creates <strong>{deptName || 'this client'}</strong>'s own Twilio subaccount under the operator master, with its own API key — encrypted and stored as this department's credentials. Nobody opens the Twilio console.</p>
              {vault === null ? (
                <div className="text-sm text-textSecondary flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Checking the operator vault…</div>
              ) : !canCreateSub ? (
                <div className="text-sm bg-danger/10 border border-danger/30 rounded-md px-3 py-2 flex gap-2">
                  <ShieldAlert size={16} className="shrink-0 mt-0.5 text-danger" />
                  <span>The operator vault can't create subaccounts yet — it needs the master account <strong>auth token</strong> (Standard API keys can't manage accounts). Add it under Trunk Setup → SMS → Master Twilio account, then come back.</span>
                </div>
              ) : twilioResult ? (
                <div className="text-sm bg-primary/10 border border-primary/30 rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-1 text-success font-medium"><CheckCircle2 size={14} /> {twilioResult.already_exists ? 'Credentials already provisioned' : 'Subaccount created'}</div>
                  <div><span className="text-textSecondary">Subaccount: </span><span className="font-mono">{twilioResult.account_sid}</span></div>
                  <div><span className="text-textSecondary">API key: </span><span className="font-mono">{twilioResult.api_key_sid}</span></div>
                </div>
              ) : (
                <button className="btn-primary inline-flex items-center gap-2" disabled={busy} onClick={createSubaccount}>
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Create the subaccount + key
                </button>
              )}
            </div>
          )}

          {key === 'numbers' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Buy phone numbers</h3>
              <p className="text-sm text-textSecondary">Numbers are bought on {twilioResult ? 'the client’s new subaccount' : 'the department’s Twilio account'} — voice + SMS capable. Buy as many as the client needs; porting existing numbers in lands later (P3).</p>
              <div className="flex gap-2 items-center">
                <input className="input max-w-[140px] font-mono" value={areaCode} maxLength={3} placeholder="989"
                  onChange={(e) => { setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3)); setFound(null); setPicked(''); }} />
                <button className="btn-ghost" disabled={searching} onClick={searchNumbers}>{searching ? 'Searching…' : 'Search numbers'}</button>
              </div>
              {found && found.length > 0 && (
                <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-auto">
                  {found.map((n: any) => (
                    <button type="button" key={n.phone_number}
                      className={`w-full text-left px-3 py-2 text-sm flex justify-between items-center hover:bg-elevated ${picked === n.phone_number ? 'bg-primary/15' : ''}`}
                      onClick={() => setPicked(n.phone_number)}>
                      <span className="font-mono">{n.phone_number}</span>
                      <span className="text-xs text-textSecondary">{[n.locality, n.region].filter(Boolean).join(', ')}{picked === n.phone_number ? '  ✓' : ''}</span>
                    </button>
                  ))}
                </div>
              )}
              {found && found.length === 0 && <div className="text-xs text-textSecondary">No available numbers — try a different area code.</div>}
              <button className="btn-primary" disabled={busy || (!picked && !areaCode)} onClick={buyNumber}>
                {busy ? 'Buying…' : picked ? `Buy ${picked}` : 'Buy first available'}
              </button>
              {bought.length > 0 && (
                <div className="text-sm space-y-1">
                  <div className="text-xs uppercase tracking-wide text-textSecondary">Purchased this onboarding</div>
                  {bought.map((n) => <div key={n.sid} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-success" /><span className="font-mono">{n.phone_number}</span></div>)}
                </div>
              )}

              {/* Port existing numbers in (P3) — Twilio runs the LOA e-sign by email */}
              <div className="border border-border rounded-lg p-3 space-y-3">
                <button type="button" className="flex items-center gap-2 text-sm font-medium w-full" onClick={() => setPortOpen(!portOpen)}>
                  <ArrowRightLeft size={15} className="text-primary" /> Port the client's existing numbers in
                  <span className="text-xs text-textSecondary font-normal">{portRequests.length ? `· ${portRequests.length} request(s)` : ''}</span>
                </button>
                {portOpen && (
                  <div className="space-y-3">
                    <L label="Numbers to port (comma or space separated)">
                      <input className="input font-mono" placeholder="+19895551234, +19895555678" value={portForm.numbers}
                        onChange={(e) => { setPortForm({ ...portForm, numbers: e.target.value }); setPortChecks(null); }} />
                    </L>
                    <button className="btn-ghost text-sm" disabled={busy || !portNums().length} onClick={checkPortability}>
                      {busy ? 'Checking…' : 'Check portability'}
                    </button>
                    {portChecks && (
                      <div className="text-xs space-y-1">
                        {portChecks.map((c) => (
                          <div key={c.phone_number} className="flex items-center gap-2">
                            <span className={c.portable ? 'text-success' : 'text-danger'}>{c.portable ? 'portable' : 'NOT portable'}</span>
                            <span className="font-mono">{c.phone_number}</span>
                            {!c.portable && <span className="text-textSecondary">{c.reason}</span>}
                            {c.pin_and_account_number_required && <span className="text-textSecondary">(PIN + account number required)</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <L label="Losing-carrier account number"><input className="input font-mono" value={portForm.account_number} onChange={(e) => setPortForm({ ...portForm, account_number: e.target.value })} /></L>
                      <L label="PIN / passcode"><input className="input font-mono" value={portForm.pin} onChange={(e) => setPortForm({ ...portForm, pin: e.target.value })} /></L>
                      <L label="Authorized person (on the losing account)"><input className="input" value={portForm.authorized_person} onChange={(e) => setPortForm({ ...portForm, authorized_person: e.target.value })} /></L>
                      <L label="Their email (Twilio sends the LOA e-sign here)"><input className="input" value={portForm.notification_email} onChange={(e) => setPortForm({ ...portForm, notification_email: e.target.value })} placeholder="owner@business.com" /></L>
                      <L label="Service street"><input className="input" value={portForm.street} onChange={(e) => setPortForm({ ...portForm, street: e.target.value })} /></L>
                      <L label="City"><input className="input" value={portForm.city} onChange={(e) => setPortForm({ ...portForm, city: e.target.value })} /></L>
                      <L label="State"><input className="input" value={portForm.region} onChange={(e) => setPortForm({ ...portForm, region: e.target.value })} placeholder="MI" /></L>
                      <L label="ZIP"><input className="input font-mono" value={portForm.postal_code} onChange={(e) => setPortForm({ ...portForm, postal_code: e.target.value })} /></L>
                    </div>
                    <button className="btn-primary text-sm" disabled={busy || !portNums().length || !portForm.notification_email} onClick={filePortIn}>
                      {busy ? 'Filing…' : `File port-in with Twilio (${portNums().length} number(s))`}
                    </button>
                    {portRequests.length > 0 && (
                      <div className="text-xs space-y-1">
                        <div className="uppercase tracking-wide text-textSecondary">Port requests</div>
                        {portRequests.map((p) => (
                          <div key={p.sid} className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <span className={/complete/i.test(p.status) ? 'text-success' : /reject|fail|cancel/i.test(p.status) ? 'text-danger' : 'text-primary'}>{p.status}</span>
                              <span className="font-mono">{(p.phone_numbers || []).join(', ')}</span>
                            </div>
                            {(p.numbers_detail || []).filter((n: any) => n.rejection_reason).map((n: any) => (
                              <div key={n.phone_number} className="text-danger pl-4">{n.phone_number}: {n.rejection_reason}</div>
                            ))}
                          </div>
                        ))}
                        <div className="text-textSecondary">Ports take days to weeks; you get an ntfy alert on rejection or completion. The client must sign the LOA email from Twilio.</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {key === 'trunk' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Auto-configure the trunk</h3>
              <p className="text-sm text-textSecondary">One click provisions the Twilio Elastic SIP Trunk (termination ACL + origination to the Stratus edge), attaches the purchased numbers, and creates the matching Stratus trunk with inbound routes.</p>
              {trunkResult ? (
                <div className="text-sm bg-primary/10 border border-primary/30 rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-1 text-success font-medium"><CheckCircle2 size={14} /> Trunk configured</div>
                  {Array.isArray(trunkResult.steps) && (
                    <ul className="text-xs space-y-0.5 mt-1">
                      {trunkResult.steps.map((s: any, i: number) => (
                        <li key={i} className={s.ok ? 'text-textSecondary' : 'text-danger'}>{s.ok ? '✓' : '✗'} {s.step}{s.detail ? ` — ${s.detail}` : ''}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <button className="btn-primary inline-flex items-center gap-2" disabled={busy || bought.length === 0} onClick={configureTrunk}>
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <PhoneCall size={14} />} Configure trunk + attach {bought.length} number(s)
                </button>
              )}
              {bought.length === 0 && !trunkResult && <p className="text-xs text-textSecondary">No numbers purchased — go back a step, or skip if the client is porting in later.</p>}
            </div>
          )}

          {key === 'e911' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">E911 dispatchable address</h3>
              <p className="text-sm text-textSecondary">Every number needs a dispatchable emergency address (Karis Law). Enter the client's locations on the trunk's E911 tab — automatic Twilio address sync ships next (P4).</p>
              <button className="btn-primary inline-flex items-center gap-2" onClick={() => window.open('/voice-chat', '_blank')}><ExternalLink size={14} /> Open the E911 tab</button>
              <div className="text-xs text-textSecondary bg-elevated border border-border rounded-md px-3 py-2 flex gap-2">
                <Info size={14} className="shrink-0 mt-0.5 text-primary" /><span>For now the addresses are stored locally; pushing them to Twilio and blocking go-live without one is tracked as wizard P4 (#512).</span>
              </div>
            </div>
          )}

          {key === 'sms' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Texting lane</h3>
              <SmsLanePicker lane={lane} onChange={(l: any) => { setLane(l); saveState(domainUuid, { step: 'sms', lane: l }); }} />
              {lane === 'quick' && <QuickLane domainUuid={domainUuid} onDone={() => { setSmsDone(true); saveState(domainUuid, { sms_done: true }); }} />}
              {lane === 'dedicated' && <DedicatedLane domainUuid={domainUuid} onDone={() => { setSmsDone(true); saveState(domainUuid, { sms_done: true }); }} />}
              {lane === 'legacy' && (
                <div className="card p-4 text-sm text-textSecondary">The client runs their own Twilio — enter their credentials under Integrations → Twilio for this department, then enable SMS in System → SMS.</div>
              )}
              {!lane && <p className="text-xs text-textSecondary">Skip if this client doesn't text.</p>}
            </div>
          )}

          {key === 'users' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Add users</h3>
              <p className="text-sm text-textSecondary">Each person automatically gets the next free extension, a SIP extension and a web login (welcome email flow). Leave blank and skip to add people later.</p>
              <div className="space-y-2">
                {users.map((u, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center">
                    <input className="input" placeholder="First name" value={u.first_name} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))} />
                    <input className="input" placeholder="Last name" value={u.last_name} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, last_name: e.target.value } : x))} />
                    <input className="input" placeholder="Email (optional)" value={u.email} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
                    <button className="btn-ghost p-1 text-danger" disabled={users.length === 1} onClick={() => setUsers(users.filter((_, j) => j !== i))}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
              <button className="btn-ghost inline-flex items-center gap-1 text-sm" onClick={() => setUsers([...users, { first_name: '', last_name: '', email: '' }])}>
                <Plus size={14} /> Add another
              </button>
              {createdUsers.length > 0 && (
                <div className="text-sm text-success">Created: {createdUsers.map((u) => `${u.name} (${u.extension})`).join(', ')}</div>
              )}
            </div>
          )}

          {key === 'guardrails' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Spend guardrails</h3>
              <p className="text-sm text-textSecondary">This subaccount bills the operator's master card and Twilio has no native per-subaccount cap. Two monthly usage triggers protect it: an <strong>alert</strong> (ntfy) and a hard <strong>auto-suspend</strong> — calls and texts stop until you reactivate the subaccount.</p>
              {guard === null ? (
                <div className="text-sm text-textSecondary flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Checking trigger state…</div>
              ) : (
                <>
                  <div className={`text-sm rounded-md px-3 py-2 flex gap-2 border ${guard.installed ? 'bg-primary/10 border-primary/30' : 'bg-danger/10 border-danger/30'}`}>
                    {guard.installed
                      ? <><CheckCircle2 size={16} className="shrink-0 mt-0.5 text-success" /><span>Guardrails are live — alert at <strong>${guard.alert_usd}</strong>, auto-suspend at <strong>${guard.suspend_usd}</strong> per month.</span></>
                      : <><ShieldAlert size={16} className="shrink-0 mt-0.5 text-danger" /><span>Triggers are NOT installed{guard.install_error ? ` — ${guard.install_error}` : ''}. Set thresholds and save to install them.</span></>}
                  </div>
                  <div className="grid grid-cols-2 gap-3 max-w-md">
                    <L label="Alert at ($/month)">
                      <input className="input font-mono" value={guardAlert} onChange={(e) => setGuardAlert(e.target.value.replace(/[^0-9.]/g, ''))} />
                    </L>
                    <L label="Auto-suspend at ($/month)">
                      <input className="input font-mono" value={guardSuspend} onChange={(e) => setGuardSuspend(e.target.value.replace(/[^0-9.]/g, ''))} />
                    </L>
                  </div>
                  <button className="btn-primary inline-flex items-center gap-2" disabled={busy || !Number(guardAlert) || !Number(guardSuspend)} onClick={saveGuardrails}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Gauge size={14} />} {guard.installed ? 'Update thresholds' : 'Install guardrails'}
                  </button>
                </>
              )}
            </div>
          )}

          {key === 'cnam' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Outbound caller-ID name (CNAM)</h3>
              <p className="text-sm text-textSecondary">Registers the name shown on the called party's phone for outbound calls. Optional. After Twilio approves, carrier CNAM databases take <strong>days to weeks</strong> to propagate — set expectations with the client.</p>
              {cnam === null ? (
                <div className="text-sm text-textSecondary flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Checking registration…</div>
              ) : cnam.registered ? (
                <div className="text-sm bg-primary/10 border border-primary/30 rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-1 font-medium"><BadgeCheck size={14} className="text-success" /> "{cnam.display_name}"</div>
                  <div><span className="text-textSecondary">Status: </span>{cnam.status}{cnam.numbers_assigned ? ` · armed on ${cnam.numbers_assigned} number(s)` : ''}</div>
                  {cnam.error && <div className="text-danger text-xs">{cnam.error}</div>}
                </div>
              ) : (
                <>
                  <L label="Display name (max 15 characters — carrier limit)">
                    <input className="input max-w-xs" maxLength={15} value={cnamName} onChange={(e) => setCnamName(e.target.value)} placeholder="ACME PLUMBING" />
                  </L>
                  <button className="btn-primary inline-flex items-center gap-2" disabled={busy || !cnamName.trim()} onClick={registerCnam}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />} Register caller ID name
                  </button>
                  <div className="text-xs text-textSecondary bg-elevated border border-border rounded-md px-3 py-2 flex gap-2">
                    <Info size={14} className="shrink-0 mt-0.5 text-primary" /><span>Needs an approved customer profile (the Dedicated SMS lane creates one). If registration says no profile exists, finish Dedicated-lane registration first or skip this step.</span>
                  </div>
                </>
              )}
            </div>
          )}

          {key === 'review' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Review — {deptName || 'client'}</h3>
              <div className="text-sm space-y-1.5">
                <RevRow ok={!!domainUuid} label="Department" detail={deptName || '—'} />
                <RevRow ok={!!twilioResult} label="Twilio subaccount" detail={twilioResult ? twilioResult.account_sid : 'not created'} mono />
                <RevRow ok={bought.length > 0 || portRequests.length > 0} label="Numbers" detail={[bought.length ? bought.map((n) => n.phone_number).join(', ') : '', portRequests.length ? `${portRequests.length} port request(s)` : ''].filter(Boolean).join(' + ') || 'none'} mono />
                <RevRow ok={!!trunkResult} label="Trunk" detail={trunkResult ? 'auto-configured + numbers attached' : 'not configured'} />
                <RevRow ok={!!e911Ready?.ready} label="E911" detail={e911Ready === null ? 'checking…' : e911Ready.ready ? `${e911Ready.synced} dispatchable address(es) synced to Twilio` : 'NO dispatchable address — add one on the trunk E911 tab before go-live'} />
                <RevRow ok={smsDone} label="SMS" detail={smsDone ? `${lane} lane provisioned` : lane ? `${lane} lane started` : 'skipped'} />
                <RevRow ok={createdUsers.length > 0} label="Users" detail={createdUsers.length ? createdUsers.map((u) => u.extension).join(', ') : 'none yet'} />
                <RevRow ok={!!guard?.installed} label="Guardrails" detail={guard?.installed ? `alert $${guard.alert_usd} / suspend $${guard.suspend_usd} per month` : 'NOT installed — go back and install them'} />
                <RevRow ok={!!cnam?.registered} label="Caller ID" detail={cnam?.registered ? `"${cnam.display_name}" · ${cnam.status}` : 'skipped'} />
              </div>
              {e911Ready !== null && !e911Ready.ready && (
                <div className="text-sm bg-danger/10 border border-danger/30 rounded-md px-3 py-2 flex gap-2">
                  <ShieldAlert size={16} className="shrink-0 mt-0.5 text-danger" />
                  <span><strong>Do not go live without E911.</strong> Kari's Law requires a dispatchable address on every number. Add the location on the trunk's E911 tab — it syncs to Twilio automatically on save.</span>
                </div>
              )}
              <p className="text-xs text-textSecondary">Everything above is saved — reopen the wizard and pick this department to continue where you left off.</p>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="p-4 border-t border-border flex items-center justify-between gap-2">
          <button className="btn-ghost inline-flex items-center gap-1" disabled={step === 0 || busy} onClick={back}>
            <ChevronLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2">
            {['twilio', 'numbers', 'trunk', 'e911', 'sms', 'guardrails', 'cnam'].includes(key) && (
              <button className="btn-ghost" disabled={busy} onClick={next}>Skip</button>
            )}
            {key === 'department' && (
              <button className="btn-primary inline-flex items-center gap-1" disabled={busy} onClick={createDepartment}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <>{pickExisting ? 'Resume' : 'Create department'} <ChevronRight size={16} /></>}
              </button>
            )}
            {['twilio', 'numbers', 'trunk', 'e911', 'sms', 'guardrails', 'cnam'].includes(key) && (
              <button className="btn-primary inline-flex items-center gap-1" disabled={busy} onClick={next}>Next <ChevronRight size={16} /></button>
            )}
            {key === 'users' && (
              <button className="btn-primary inline-flex items-center gap-1" disabled={busy} onClick={createUsers}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <>Create users <ChevronRight size={16} /></>}
              </button>
            )}
            {key === 'review' && <button className="btn-primary inline-flex items-center gap-1" onClick={finish}>Done <CheckCircle2 size={16} /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }: { label: string; children: any }) {
  return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>;
}

function RevRow({ ok, label, detail, mono }: { ok: boolean; label: string; detail: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-2">
      <span className={`mt-0.5 ${ok ? 'text-success' : 'text-textSecondary'}`}>{ok ? <CheckCircle2 size={14} /> : <span className="inline-block w-3.5 text-center">–</span>}</span>
      <span className="w-36 shrink-0 text-textSecondary">{label}</span>
      <span className={mono ? 'font-mono text-xs mt-0.5' : ''}>{detail}</span>
    </div>
  );
}
